/* ================================
  predictive-search.js (Premium Drawer)
  - products only
  - max 4 products shown
  - scroll inside drawer
  - fade back to MOST SEARCHED when input clears
  - word-start relevance filter (so no "bed" on "b" unless you actually have it)
================================ */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.resultsHost = this.querySelector('[data-predictive-search]');
    this.statusEl = this.querySelector('.predictive-search-status');

    this.abortController = null;
    this.cache = new Map();
    this.activeIndex = -1;

    this._onInput = this._debounce(this._onInput.bind(this), 140);
    this._onKeydown = this._onKeydown.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
  }

  connectedCallback() {
    if (!this.input || !this.resultsHost) return;

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('keydown', this._onKeydown);
    this.addEventListener('focusout', this._onFocusOut);
    this.input.addEventListener('search', () => {
  if (this.query.length) this._open();
  });
  }

  disconnectedCallback() {
    if (!this.input) return;

    this.input.removeEventListener('input', this._onInput);
    this.input.removeEventListener('keydown', this._onKeydown);
    this.removeEventListener('focusout', this._onFocusOut);

    if (this.abortController) this.abortController.abort();
  }

  get query() {
    return (this.input?.value || '').trim();
  }

  /* ========= Fade swap MOST SEARCHED <-> Predictive ========= */

  _getMostSearchedEl() {
    // Your MOST SEARCHED wrapper in search-drawer.liquid
    return this.closest('search-drawer')?.querySelector('.sd__most');
  }

  _fadeSwap(showPredictive) {
    const most = this._getMostSearchedEl();
    const host = this.resultsHost;
    if (!host) return;

    if (showPredictive) {
      most?.classList.add('sd-fade-out');
      most?.classList.remove('sd-fade-in');

      host.classList.add('sd-fade-in');
      host.classList.remove('sd-fade-out');
    } else {
      host.classList.add('sd-fade-out');
      host.classList.remove('sd-fade-in');

      most?.classList.add('sd-fade-in');
      most?.classList.remove('sd-fade-out');
    }
  }

  /* ========= Main input ========= */

  async _onInput() {
    const q = this.query;

    if (!q.length) {
      this._fadeSwap(false); // show MOST SEARCHED
      this._close(true);
      return;
    }

    this._fadeSwap(true);

    // premium loading state
    this._setLoading(true);
    this._announce('Loading');

    const key = q.toLowerCase();
    const cached = this.cache.get(key);
    if (cached) {
      this._render(cached, q);
      this._setLoading(false);
      return;
    }

    // Abort previous request
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const url =
        `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search` +
        `&resources[type]=product` +
        `&resources[limit]=12`;

      const res = await fetch(url, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(String(res.status));

      const text = await res.text();
      const inner =
        new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML || '';

      this.cache.set(key, inner);
      this._render(inner, q);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      this._close(false);
      this._announce('No results');
      this._fadeSwap(false);
    } finally {
      this._setLoading(false);
    }
  }

  _render(markup, rawQuery) {
    this.resultsHost.innerHTML = markup;

    // premium relevance filter + max 4 products
    this._filterResults(rawQuery);

    // If nothing visible after filter -> close + show MOST SEARCHED
    if (!this._hasAnyVisibleOption()) {
      this._close(false);
      this._announce('No results');
      this._fadeSwap(false);
      return;
    }

    this._fadeSwap(true);

    // Open + size for drawer scrolling
    this._open();
    this._setMaxHeight();

    // Reset keyboard selection
    this.activeIndex = -1;
    this._clearActiveDescendant();

    // SR announce (optional)
    const countText = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent?.trim();
    if (countText) this._announce(countText);
  }

  /* ========= Relevance filtering ========= */

  _filterResults(rawQuery) {
    const q = (rawQuery || '').trim().toLowerCase();
    if (!q) return;

    // WORD-START only:
    // - startsWith(q) OR any word startsWith(q)
    const wordStartMatch = (text) => {
      const t = (text || '').toLowerCase().trim();
      if (!t) return false;
      if (t.startsWith(q)) return true;
      const words = t.split(/[^a-z0-9]+/).filter(Boolean);
      return words.some((w) => w.startsWith(q));
    };

    // Suggestions group (if present)
    const suggGroup = this.querySelector('[data-ps-group="suggestions"]');
    const suggList = this.querySelector('[data-ps-list="suggestions"]');
    if (suggGroup && suggList) {
      const items = Array.from(suggList.querySelectorAll('li.predictive-search__list-item'));
      items.forEach((li) => {
        const a = li.querySelector('a');
        const text = a?.textContent || '';
        li.style.display = wordStartMatch(text) ? '' : 'none';
      });

      // Hide group if empty after filter
      const anyVisible = items.some((li) => li.style.display !== 'none');
      suggGroup.style.display = anyVisible ? '' : 'none';
    }

    // Products group: filter + keep only first 4
    const prodGroup = this.querySelector('[data-ps-group="products"]');
    const prodList = this.querySelector('[data-ps-list="products"]');
    if (prodGroup && prodList) {
      const items = Array.from(prodList.querySelectorAll('li.predictive-search__list-item'));
      let kept = 0;

      items.forEach((li) => {
        const a = li.querySelector('[data-ps-title]') || li.querySelector('a');
        const title = a?.getAttribute?.('data-ps-title') || a?.textContent || '';
        const ok = wordStartMatch(title);

        if (ok && kept < 4) {
          li.style.display = '';
          kept++;
        } else {
          li.style.display = 'none';
        }
      });

      prodGroup.style.display = kept > 0 ? '' : 'none';
    }
  }

  _hasAnyVisibleOption() {
    return Array.from(this.querySelectorAll('li.predictive-search__list-item')).some(
      (li) => li.style.display !== 'none'
    );
  }

  /* ========= Drawer sizing ========= */

  _setMaxHeight() {
    // Keep scrolling inside drawer results area
    const max = Math.max(220, window.innerHeight - 220);
    this.resultsHost.style.maxHeight = `${max}px`;
    this.resultsHost.style.overflow = 'auto';
    this.resultsHost.style.webkitOverflowScrolling = 'touch';
  }

  /* ========= Open / Close / Loading ========= */

  _open() {
    this.setAttribute('open', 'true');
    this.input?.setAttribute('aria-expanded', 'true');
  }

  _close(clearHost) {
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input?.setAttribute('aria-expanded', 'false');
    this._clearActiveDescendant();

    if (clearHost) this.resultsHost.innerHTML = '';
  }

  _setLoading(isLoading) {
    if (isLoading) this.setAttribute('loading', 'true');
    else this.removeAttribute('loading');
  }

  _announce(text) {
    if (!this.statusEl) return;
    this.statusEl.setAttribute('aria-hidden', 'false');
    this.statusEl.textContent = text || '';
    window.setTimeout(() => this.statusEl.setAttribute('aria-hidden', 'true'), 600);
  }

  /* ========= Focus & Keyboard ========= */

  _onFocusOut() {
  window.setTimeout(() => {
    // iPhone "Done" blurs input — don't hide results if there's still a query
    if (this.query.length) {
      this._open(); // keep [open] so CSS keeps results visible
      return;
    }

    // Only close if query is empty AND focus actually left the component
    if (!this.contains(document.activeElement)) this._close(false);
  }, 80);
}

  _onKeydown(e) {
    if (!this.hasAttribute('open')) return;

    const options = this._getVisibleOptions();
    if (!options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % options.length;
      this._setActiveOption(options);
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + options.length) % options.length;
      this._setActiveOption(options);
    }

    if (e.key === 'Enter') {
      const li = options[this.activeIndex];
      const link = li?.querySelector('a');
      if (link) link.click();
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      this._close(false);
      this.input?.blur?.();
    }
  }

  _getVisibleOptions() {
    return Array.from(this.querySelectorAll('li.predictive-search__list-item')).filter(
      (li) => li.style.display !== 'none'
    );
  }

  _setActiveOption(options) {
    options.forEach((li) => li.setAttribute('aria-selected', 'false'));

    const active = options[this.activeIndex];
    if (!active) return;

    active.setAttribute('aria-selected', 'true');
    if (active.id) this.input?.setAttribute('aria-activedescendant', active.id);

    active.scrollIntoView({ block: 'nearest' });
  }

  _clearActiveDescendant() {
    this.activeIndex = -1;
    this.input?.setAttribute('aria-activedescendant', '');
    this.querySelectorAll('[aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
  }

  /* ========= Utils ========= */

  _debounce(fn, wait) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
